import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Sparkles, Send, X, Loader2 } from 'lucide-react';

const API = `${window.__BACKEND_URL__}/api`;

export default function AskHinesTime() {
  const { token, isAdmin, isSupervisor } = useAuth();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [sessionId] = useState(() => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}`));
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, open]);

  // Other screens (e.g. the payroll push result) can open the assistant with a ready-made question.
  useEffect(() => {
    const onAsk = (e) => { const q = e.detail?.question; if (q) { setOpen(true); sendText(q); } };
    window.addEventListener('ht:ask', onAsk);
    return () => window.removeEventListener('ht:ask', onAsk);
  }, [token, busy]); // eslint-disable-line

  const sendText = async (q) => {
    if (!q || busy) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', text: q }]);
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/ask`, { question: q, session_id: sessionId }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessages((m) => [...m, { role: 'assistant', text: data.answer }]);
    } catch (e) {
      setMessages((m) => [...m, { role: 'assistant', text: e.response?.data?.detail || 'Sorry, something went wrong — please try again.' }]);
    } finally { setBusy(false); }
  };
  const send = () => sendText(input.trim());

  if (!isAdmin && !isSupervisor) return null;

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-20 right-4 md:bottom-24 md:right-5 z-40 flex items-center gap-2 px-3 py-2 md:px-4 md:py-3 rounded-full shadow-xl font-semibold text-xs md:text-sm text-white"
          title="Ask HinesTime AI"
          style={{ background: 'linear-gradient(135deg, #1E88E5, #7C3AED)' }}
          data-testid="ask-hinestime-fab"
        >
          <Sparkles className="w-4 h-4" /> Ask HinesTime
        </button>
      )}
      {open && (
        <div className="fixed bottom-5 right-5 z-50 w-[380px] max-w-[92vw] rounded-2xl shadow-2xl border flex flex-col"
          style={{ background: '#0F1923', borderColor: '#2D3748', height: '520px' }} data-testid="ask-hinestime-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: '#2D3748' }}>
            <span className="flex items-center gap-2 font-semibold text-white text-sm"><Sparkles className="w-4 h-4 text-[#7C3AED]" /> Ask HinesTime</span>
            <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-white" data-testid="ask-close-btn"><X className="w-4 h-4" /></button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.length === 0 && (
              <div className="text-xs text-slate-400 space-y-2">
                <p>Ask me anything about your workforce data, e.g.:</p>
                {["Who worked at the Kelowna site last Tuesday?", "How many hours did Test Worker log this week?", "Who's clocked in right now?", "Who's scheduled tomorrow?"].map((s) => (
                  <button key={s} onClick={() => setInput(s)} className="block w-full text-left px-3 py-2 rounded-lg border text-slate-300 hover:border-blue-500/50"
                    style={{ borderColor: '#2D3748' }}>{s}</button>
                ))}
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`text-sm whitespace-pre-wrap rounded-xl px-3 py-2 max-w-[85%] ${m.role === 'user' ? 'ml-auto text-white' : 'text-gray-200'}`}
                style={{ background: m.role === 'user' ? '#1E88E5' : '#1A2433' }}
                data-testid={`ask-msg-${m.role}-${i}`}>
                {m.text}
              </div>
            ))}
            {busy && <div className="flex items-center gap-2 text-xs text-slate-400"><Loader2 className="w-3 h-3 animate-spin" /> Thinking…</div>}
            <div ref={endRef} />
          </div>
          <div className="p-3 border-t flex gap-2" style={{ borderColor: '#2D3748' }}>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && send()}
              placeholder="Ask a question…"
              className="flex-1 px-3 py-2 rounded-lg text-sm text-white outline-none"
              style={{ background: '#1A2433', border: '1px solid #2D3748' }}
              data-testid="ask-input"
            />
            <button onClick={send} disabled={busy} className="px-3 rounded-lg text-white" style={{ background: '#1E88E5' }} data-testid="ask-send-btn">
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
